import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Alert,
    AlertTitle,
    Box
} from '@mui/material';
import { Warning, Info, Error } from '@mui/icons-material';

export interface AlertDialogProps {
    open: boolean;
    title: string;
    message: string;
    severity?: 'info' | 'warning' | 'error';
    onClose: () => void;
    onRetry?: () => void;
    actionType?: 'retry' | 'close';
}

const AlertDialog: React.FC<AlertDialogProps> = ({
    open,
    title,
    message,
    severity = 'info',
    onClose,
    onRetry,
    actionType = 'retry'
}) => {
    const getSeverityIcon = () => {
        switch (severity) {
            case 'error':
                return <Error color="error" />;
            case 'warning':
                return <Warning color="warning" />;
            case 'info':
            default:
                return <Info color="info" />;
        }
    };

    const getSeverityColor = () => {
        switch (severity) {
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
            default:
                return 'info';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'background.paper' }}>
                {getSeverityIcon()}
                {title}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: 'background.paper' }}>
                <DialogContentText id="alert-dialog-description">
                    <Box sx={{ mt: 1 }}>
                        <Alert severity={getSeverityColor()} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                            <AlertTitle>{title}</AlertTitle>
                            {message}
                        </Alert>
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: 'background.paper' }}>
                {actionType === 'retry' && (
                    <Button onClick={onRetry} color="primary" variant="contained" autoFocus>
                        Try Again
                    </Button>
                )}
                {actionType === 'close' && (
                    <Button onClick={onClose} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AlertDialog;
